import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { ShieldCheck, Search, FileCheck, Home as HomeIcon } from "lucide-react";
import { images, agent } from "../mock";
import PageHero from "../components/PageHero";
import ContactForm from "../components/ContactForm";
import { LenderCard } from "../components/Lender";

const steps = [
  { icon: ShieldCheck, title: "Get Qualified", body: "Connect with my flexible lender for a soft, no-impact pre-approval — even with credit concerns." },
  { icon: Search, title: "Find Your Home", body: "We tour the Corridor together and hunt down the one that feels right and fits your budget." },
  { icon: FileCheck, title: "Make the Offer", body: "I negotiate hard on your behalf and guide the paperwork with zero overwhelm." },
  { icon: HomeIcon, title: "Get the Keys", body: "We cross the finish line at closing — and I hand you the keys to your new front door." },
];

const ApplyToBuy = () => {
  const [searchParams] = useSearchParams();
  const area = searchParams.get("area");
  const initialMessage = area
    ? `I'm interested in homes in ${area}. Please send me listings that match what I'm looking for.`
    : "";

  useEffect(() => {
    if (area) {
      const t = setTimeout(() => {
        const el = document.getElementById("buyer-application");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 400);
      return () => clearTimeout(t);
    }
  }, [area]);

  return (
    <div>
      <PageHero
        eyebrow="Let's Get Started"
        title="Apply to Buy"
        subtitle="A warm, casual-luxury home-buying experience — start with a few quick details below."
        image={images.interiors[0]}
      />

      <section className="max-w-7xl mx-auto px-6 md:px-8 py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-xs tracking-luxury uppercase text-gold">How It Works</span>
          <h2 className="font-display text-4xl md:text-5xl text-[#141210] mt-4">
            From "someday" to "sold" in four steps
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-7">
          {steps.map((s, i) => (
            <div key={s.title} className="bg-white rounded-2xl p-8 border border-[#efe6d6] hover-lift">
              <div className="w-12 h-12 rounded-full bg-[#f7edcf] flex items-center justify-center mb-5">
                <s.icon className="w-6 h-6 text-gold" />
              </div>
              <div className="text-xs tracking-luxury uppercase text-gold mb-2">Step {i + 1}</div>
              <h3 className="font-display text-2xl text-[#141210] mb-2">{s.title}</h3>
              <p className="text-sm text-[#5c5348] leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#f4f1ea]">
        <div className="max-w-4xl mx-auto px-6 md:px-8 py-24">
          <div className="text-center mb-12">
            <span className="text-xs tracking-luxury uppercase text-gold">Get Pre-Approved</span>
            <h2 className="font-display text-4xl md:text-5xl text-[#141210] mt-4">
              Ready to apply now?
            </h2>
            <p className="text-[#5c5348] mt-4 max-w-xl mx-auto">
              Start a fast, secure application with my trusted lending partner — attributed to your
              referral from me.
            </p>
          </div>
          <LenderCard />
        </div>
      </section>

      <section id="buyer-application" className="max-w-4xl mx-auto px-6 md:px-8 py-24">
        <div className="text-center mb-12">
          <span className="text-xs tracking-luxury uppercase text-gold">Buyer Application</span>
          <h2 className="font-display text-4xl md:text-5xl text-[#141210] mt-4">
            {area ? `Let's find your home in ${area}` : "Tell me about your dream"}
          </h2>
          <p className="text-[#5c5348] mt-4">
            {area
              ? "I've noted your area below — add a few details and I'll personally send you matching listings."
              : "Or reach me directly at "}
            {!area && (
              <a href={agent.phoneHref} className="text-gold hover:underline">{agent.phone}</a>
            )}
          </p>
        </div>
        <div className="bg-white rounded-3xl p-8 md:p-11 shadow-[0_30px_70px_-45px_rgba(84,64,34,0.5)] border border-[#efe6d6]">
          <ContactForm type="apply" initialMessage={initialMessage} />
        </div>
      </section>
    </div>
  );
};

export default ApplyToBuy;
